/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, ChevronRight, Clock, Filter, LogIn, LogOut, RefreshCw, X } from "lucide-react";
import { visitorsApi } from "../visitors/api";
import { toast } from "../ui";
import "./VisitorOpsPage.css";

const STATUS_OPTS = [
  { value: "", label: "All" },
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "INSIDE", label: "Inside" },
  { value: "LEFT", label: "Left" },
  { value: "DENIED", label: "Denied" },
];

const STATUS_CLASS = {
  PENDING: "vo-pill--pending",
  APPROVED: "vo-pill--approved",
  INSIDE: "vo-pill--inside",
  LEFT: "vo-pill--muted",
  DENIED: "vo-pill--denied",
  CANCELLED: "vo-pill--muted",
};

function fmtAgo(iso) {
  if (!iso) return "";
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
  } catch (_) { return ""; }
}

export default function VisitorOpsPage() {
  const [statusFilter, setStatusFilter] = useState("");
  const [date, setDate] = useState("");
  const [visitors, setVisitors] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busy, setBusy] = useState(null);
  const [overrideModal, setOverrideModal] = useState(null); // visitor object

  const load = useCallback(async (opts = {}) => {
    const { append = false, cur = null } = opts;
    if (!append) setLoading(true);
    else setLoadingMore(true);
    try {
      const data = await visitorsApi.listVisitors({
        status: statusFilter || undefined,
        date: date || undefined,
        cursor: cur || undefined,
        limit: 25,
      });
      const items = data.items || [];
      setVisitors((v) => append ? [...v, ...items] : items);
      const nextCursor = data.next_cursor || null;
      setCursor(nextCursor);
      setHasMore(!!nextCursor);
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not load visitors");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [statusFilter, date]);

  useEffect(() => {
    setVisitors([]);
    setCursor(null);
    load();
  }, [load]);

  const handleOverride = async (note) => {
    if (!overrideModal) return;
    setBusy(overrideModal.id);
    try {
      await visitorsApi.overrideVisitor(overrideModal.id, { override_note: note });
      toast.success("Visitor approved");
      setOverrideModal(null);
      load();
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not approve");
    } finally {
      setBusy(null);
    }
  };

  const handleCheckIn = async (v) => {
    setBusy(v.id);
    try {
      await visitorsApi.checkIn(v.id);
      toast.success("Checked in");
      load();
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not check in");
    } finally {
      setBusy(null);
    }
  };

  const handleCheckOut = async (v) => {
    setBusy(v.id);
    try {
      await visitorsApi.checkOut(v.id);
      toast.success("Checked out");
      load();
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not check out");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="vo-page">
      <div className="vo-head">
        <h1>Visitor Log</h1>
        <button className="vo-refresh" onClick={() => load()} disabled={loading}>
          <RefreshCw size={15} />
        </button>
      </div>

      {/* Filters */}
      <div className="vo-filters">
        <div className="vo-chips">
          {STATUS_OPTS.map((opt) => (
            <button
              key={opt.value}
              className={`vo-chip${statusFilter === opt.value ? " active" : ""}`}
              onClick={() => setStatusFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <label className="vo-date-filter">
          <Clock size={13} />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          {date && (
            <button className="vo-clear-date" onClick={() => setDate("")}>
              <X size={12} />
            </button>
          )}
        </label>
      </div>

      {/* List */}
      {loading ? (
        <div className="vo-loading">Loading…</div>
      ) : visitors.length === 0 ? (
        <div className="vo-empty">No visitors found.</div>
      ) : (
        <div className="vo-list">
          {visitors.map((v) => (
            <div key={v.id} className="vo-row">
              <div className="vo-row-main">
                <div className="vo-row-name">{v.name || "—"}</div>
                <div className="vo-row-meta">
                  {v.villa_number ? `${v.villa_number}` : ""}
                  {v.villa_number && v.kind ? " · " : ""}
                  {v.kind === "WALK_UP" ? "Walk-up" : v.kind === "INVITE" ? "Invited" : v.kind || ""}
                  {" · "}{fmtAgo(v.created_at)}
                </div>
              </div>
              <div className="vo-row-right">
                <span className={`vo-pill ${STATUS_CLASS[v.status] || ""}`}>{v.status}</span>
                {v.status === "PENDING" && (
                  <button
                    className="vo-action-btn vo-action-btn--approve"
                    disabled={busy === v.id}
                    onClick={() => setOverrideModal(v)}
                  >
                    Approve
                  </button>
                )}
                {v.status === "APPROVED" && (
                  <button
                    className="vo-action-btn"
                    disabled={busy === v.id}
                    onClick={() => handleCheckIn(v)}
                  >
                    <LogIn size={13} /> Check in
                  </button>
                )}
                {v.status === "INSIDE" && (
                  <button
                    className="vo-action-btn"
                    disabled={busy === v.id}
                    onClick={() => handleCheckOut(v)}
                  >
                    <LogOut size={13} /> Check out
                  </button>
                )}
              </div>
            </div>
          ))}
          {hasMore && (
            <button
              className="vo-load-more"
              disabled={loadingMore}
              onClick={() => load({ append: true, cur: cursor })}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          )}
        </div>
      )}

      {/* Override approval modal */}
      {overrideModal && (
        <OverrideModal
          visitor={overrideModal}
          onClose={() => setOverrideModal(null)}
          onConfirm={handleOverride}
          busy={busy === overrideModal.id}
        />
      )}
    </div>
  );
}

function OverrideModal({ visitor, onClose, onConfirm, busy }) {
  const [note, setNote] = useState("");
  return (
    <div className="vo-modal-overlay" onClick={onClose}>
      <div className="vo-modal" onClick={(e) => e.stopPropagation()}>
        <button className="vo-modal-close" onClick={onClose}><X size={16} /></button>
        <h3>Approve visitor</h3>
        <p className="vo-modal-name">{visitor.name}</p>
        <p className="vo-modal-hint">An override note is required (audit log).</p>
        <textarea
          className="vo-modal-note"
          rows={3}
          placeholder="Reason for override…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          autoFocus
        />
        <div className="vo-modal-actions">
          <button className="vo-modal-cancel" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="vo-modal-confirm"
            disabled={!note.trim() || busy}
            onClick={() => onConfirm(note.trim())}
          >
            {busy ? "Approving…" : "Approve"}
          </button>
        </div>
      </div>
    </div>
  );
}
